import React, { useEffect, useState } from 'react';
import { Play } from 'lucide-react';
import { Modal, errorList } from '../ExerciseLibrary/shared';
import { prescriptionLine } from './MyPlanGeneratorDialog';
import { fetchTodayRecovery, firstPlayableDay, generateTodayProposal, saveTodayWorkout, trainedTodayMuscles } from './todayWorkout';
import '../../styles/ui.css';
import '../ExerciseLibrary/ExerciseLibrary.css';

/*
 * «Gerar o treino de hoje» — o atleta pede UMA sessão para agora, com o tempo que tem.
 *
 * O motor de planos e a gravação vivem em `todayWorkout.js`: o mesmo pedido que o hub faz quando o atleta muda
 * o tempo no próprio ecrã do treino de hoje. Aqui só há os parâmetros (objetivo, experiência, tempo) e a
 * pré-visualização do que o motor devolveu — nada se corta nem se reescreve no cliente.
 *
 * Nada é gravado até o atleta carregar em «Guardar e começar»; aí o plano é criado no nome dele (o servidor
 * força o atleta da sessão) e a sessão arranca no player, com o dia que o servidor devolveu. Sem histórico não
 * há cargas: o gerador não as inventa e a lista não escreve nenhuma.
 */

const GOALS = [
  ['hypertrophy', 'Hipertrofia'],
  ['strength', 'Força'],
  ['endurance', 'Resistência'],
  ['general', 'Geral'],
];
const LEVELS = [['beginner', 'Iniciante'], ['intermediate', 'Intermédio'], ['advanced', 'Avançado']];

export default function TodayWorkoutDialog({ onClose, onStart }) {
  const [form, setForm] = useState({ goal: 'hypertrophy', experience: 'beginner', sessionMinutes: 60, seed: 0 });
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [recovery, setRecovery] = useState({ state: 'loading', muscles: [] });

  // O que ele já treinou HOJE — um facto do histórico (dias desde o último treino), não um limiar inventado.
  useEffect(() => {
    let alive = true;
    fetchTodayRecovery().then((r) => { if (alive) setRecovery(r); });
    return () => { alive = false; };
  }, []);

  const trainedToday = trainedTodayMuscles(recovery);
  const avoidMuscles = trainedToday.map((m) => m.muscleId);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const generate = async (seed = form.seed) => {
    setBusy(true);
    setErrors([]);
    try {
      const data = await generateTodayProposal({
        goal: form.goal,
        experience: form.experience,
        sessionMinutes: form.sessionMinutes,
        avoidMuscles,
        seed,
      });
      setForm((f) => ({ ...f, seed }));
      setPlan(data);
    } catch (e) {
      // Fica nos parâmetros, diz o que falhou e o botão continua lá para repetir.
      setErrors(errorList(e, 'Não foi possível gerar o treino de hoje. Tente outra vez.'));
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    setSaving(true);
    setErrors([]);
    try {
      const res = await saveTodayWorkout(plan);
      // Sem sessão confirmada, ou sem o dia devolvido pelo servidor: diz-se o que se passou e o botão continua lá.
      if (!res.ok) { setErrors([res.message]); return; }
      onStart(res.dayId);
    } catch (e) {
      // A proposta fica à vista e «Guardar e começar» continua lá para repetir.
      setErrors(errorList(e, 'Não foi possível guardar o treino. Tente outra vez.'));
    } finally {
      setSaving(false);
    }
  };

  const days = plan?.days || [];
  const { day: today, index: todayIndex } = firstPlayableDay(days);
  const exercises = today?.exercises || [];
  const total = exercises.length;
  // O motor só aceita 30 a 120 minutos: fora disso o pedido seria recusado, por isso não se chega a pedi-lo.
  const minutesOk = Number(form.sessionMinutes) >= 30 && Number(form.sessionMinutes) <= 120;

  return (
    <Modal
      title="Treino de hoje"
      onClose={onClose}
      footer={plan ? (
        <>
          <button type="button" className="lib-btn" onClick={() => { setPlan(null); setErrors([]); }} disabled={busy || saving}>← Parâmetros</button>
          <button type="button" className="lib-btn" onClick={() => generate(form.seed + 1)} disabled={busy || saving}>Outra proposta</button>
          <button type="button" className="lib-btn primary" onClick={start} disabled={busy || saving || total === 0} data-testid="today-workout-use">
            {saving ? 'A guardar…' : <><Play size={16} aria-hidden="true" /> Guardar e começar</>}
          </button>
        </>
      ) : (
        <>
          <button type="button" className="lib-btn" onClick={onClose}>Cancelar</button>
          <button type="button" className="lib-btn primary" onClick={() => generate()} disabled={busy || !minutesOk} data-testid="today-workout-generate">
            {busy ? 'A gerar…' : 'Gerar treino de hoje'}
          </button>
        </>
      )}
    >
      {errors.length > 0 && (
        <div className="lib-notice bad" role="alert" data-testid="today-workout-errors">
          <ul>{errors.map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}

      {!plan && (
        <div data-testid="today-workout-params">
          <p className="lib-hint" style={{ marginTop: 0 }}>
            Propomos <strong>uma sessão para hoje</strong>, com o objetivo, a experiência e o tempo que tiver.
            O motor só propõe planos de 2 a 6 dias: pedimos a proposta mais curta e treina hoje a primeira sessão
            dela — a segunda fica no seu plano, para outro dia. Nada é gravado até carregar em «Guardar e começar».
          </p>
          <div className="lib-field">
            <label className="lib-label" htmlFor="today-goal">Objetivo</label>
            <select id="today-goal" className="lib-select" value={form.goal} onChange={(e) => set({ goal: e.target.value })}>
              {GOALS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="lib-field">
            <label className="lib-label" htmlFor="today-level">Experiência</label>
            <select id="today-level" className="lib-select" value={form.experience} onChange={(e) => set({ experience: e.target.value })}>
              {LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="lib-field">
            <label className="lib-label" htmlFor="today-minutes">Tempo que tem agora (min)</label>
            <input id="today-minutes" className="lib-input" type="number" min={30} max={120} step={5} value={form.sessionMinutes}
              onChange={(e) => set({ sessionMinutes: e.target.value === '' ? '' : Number(e.target.value) })} />
            <span className="lib-hint">Entre 30 e 120 minutos, aquecimento incluído.</span>
          </div>
          <p className="lib-hint" data-testid="today-workout-avoid" role="status">
            {recovery.state === 'loading' ? 'A confirmar o que já treinou hoje…' : recovery.state === 'error'
              ? 'Não foi possível confirmar o que já treinou hoje: a proposta pode voltar a pedir os mesmos músculos.'
              : trainedToday.length === 0
                ? 'Ainda não treinou nada hoje: nenhum músculo fica de fora.'
                : `Fica de fora o que já treinou hoje: ${trainedToday.map((m) => m.name).join(', ')}.`}
          </p>
        </div>
      )}

      {plan && (
        <div data-testid="today-workout-preview">
          <div className="lib-banner" role="status">
            <strong>{today?.name || plan.title}</strong>
            {plan.split ? <span>{plan.split}</span> : null}
            <span>{today ? `~${today.estimatedMinutes} min` : plan.title} · {total} exercícios</span>
          </div>

          {recovery.state === 'ok' && trainedToday.length > 0 ? (
            <p className="lib-hint" data-testid="today-workout-avoided">
              Fora da proposta, por já os ter treinado hoje: {trainedToday.map((m) => m.name).join(', ')}.
            </p>
          ) : null}

          {plan.warnings?.length > 0 && (
            <div className="lib-notice warn" role="status" data-testid="today-workout-warnings">
              <ul>{plan.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
            </div>
          )}

          <section className="lib-section" aria-label={today?.name || 'Sessão de hoje'} data-testid="today-workout-day">
            <h3 style={{ margin: '0 0 2px', fontSize: '1.02rem' }}>{today?.name || 'Sessão de hoje'}</h3>
            <div className="lib-hint">{today?.focus ? `${today.focus} · ` : ''}{today ? `~${today.estimatedMinutes} min` : ''}</div>
            {total === 0 ? (
              <p className="lib-hint">Esta sessão ficou sem exercícios com as restrições indicadas. Tente outra proposta.</p>
            ) : (
              <ol className="gm-list" style={{ margin: 0, padding: 0 }}>
                {exercises.map((ex, i) => (
                  <li key={`${ex.exerciseLibraryId}-${i}`} className="gm-list-row">
                    <span className="gm-list-main">
                      <span className="gm-list-title gm-list-name">{ex.name}</span>
                      <span className="gm-list-sub">{prescriptionLine(ex) || 'Sem prescrição'}</span>
                      <span className="gm-list-sub">{ex.groupLabel}{ex.equipment ? ` · ${ex.equipment}` : ''}</span>
                      {ex.fromHistory && ex.targetWeightKg != null && ex.progressionNote ? (
                        <span className="gm-list-sub">{ex.progressionNote}{ex.progressed ? ' 📈' : ''}</span>
                      ) : null}
                      {ex.reason ? <span className="gm-list-sub">Porquê: {ex.reason}</span> : null}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>

          {days.length > 1 && todayIndex >= 0 ? (
            <p className="lib-hint">
              A proposta tem {days.length} sessões; hoje é a {todayIndex + 1}.ª. As outras ficam no plano que vai ser
              guardado no seu nome, e pode treiná-las no separador Plano.
            </p>
          ) : null}

          {plan.notes?.length > 0 && (
            <section aria-label="Notas do gerador" data-testid="today-workout-notes">
              <h3 style={{ fontSize: '1.02rem' }}>Notas</h3>
              <ul style={{ margin: 0, paddingLeft: 20 }}>{plan.notes.map((n) => <li key={n}>{n}</li>)}</ul>
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}
